import { useTranslate } from '@tolgee/react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha, withGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { TournamentCategoryItemFragment } from '@amf/shared/types/graphql';
import { TournamentGroundType, TournamentPlayType } from '@amf/shared/data/Tournament';
import { Coords } from '@amf/shared/hooks/useCoordsAddress';
import { Regions } from '@amf/shared/utils/region';
import TextField from '@amf/shared/components/form/TextField';
import SelectField from '@amf/shared/components/form/SelectField';
import TextArea from '@amf/shared/components/form/TextArea';
import Button from '@amf/shared/components/button/Button';
import { REGEX_PHONE_NUMBER } from '@amf/shared/utils/string';

import TournamentFormMap from 'components/tournament/TournamentFormMap';

const schema = yup.object({
  givenName: yup
    .string()
    .required('tournaments.form.givenName.required')
    .max(16, 'tournaments.form.givenName.max'),
  familyName: yup
    .string()
    .required('tournaments.form.familyName.required')
    .max(16, 'tournaments.form.familyName.max'),
  email: yup
    .string()
    .email('tournaments.form.email.invalid')
    .required('tournaments.form.email.required'),
  phone: yup
    .string()
    .nullable()
    .transform((v, o) => (o === '' ? null : v))
    .matches(REGEX_PHONE_NUMBER, 'tournaments.form.phone.invalid'),
  category: yup.string(),
  name: yup
    .string()
    .required('tournaments.form.name.required')
    .max(128, 'tournaments.form.name.max'),
  year: yup
    .string()
    .nullable()
    .transform((_, value) => (value === '' ? null : value)),
  playType: yup.string().required('tournaments.form.playType.required'),
  groundType: yup.string().required('tournaments.form.groundType.required'),
  description: yup
    .string()
    .required('tournaments.form.description.required')
    .max(512, 'tournaments.form.description.max'),
  link: yup
    .string()
    .url('tournaments.form.link.invalid')
    .nullable()
    .transform((_, value) => (value === '' ? null : value))
    .max(512, 'tournaments.form.link.max'),
  fee: yup
    .number()
    .required('tournaments.form.fee.required')
    .typeError('tournaments.form.fee.required')
    .min(0, 'tournaments.form.fee.positive'),
  region: yup.string().required('tournaments.form.region.required'),
  city: yup
    .string()
    .required('tournaments.form.city.required')
    .max(128, 'tournaments.form.city.min'),
  dateFrom: yup.string().required('tournaments.form.dateFrom.required'),
  dateTo: yup
    .string()
    .nullable()
    .transform((_, value) => (value === '' ? null : value)),
  latitude: yup
    .number()
    .required('tournaments.form.address.required')
    .typeError('tournaments.form.address.required')
    .transform((_, value) => (value === '' ? null : parseFloat(value))),
  longitude: yup
    .number()
    .required('tournaments.form.address.required')
    .typeError('tournaments.form.address.required')
    .transform((_, value) => (value === '' ? null : parseFloat(value))),
});

export interface TournamentFormData {
  givenName: string;
  familyName: string;
  email: string;
  phone: string | null;
  category: string;
  name: string;
  year: string | null;
  playType: string;
  groundType: string;
  description: string;
  link: string | null;
  fee: number;
  region: string;
  city: string;
  dateFrom: string;
  dateTo: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface TournamentFormProps {
  values: TournamentFormData;
  onSubmit: (values: TournamentFormData) => Promise<void>;
  categories: TournamentCategoryItemFragment[];
  disableAuthor?: boolean;
}

function TournamentForm({ categories, values, onSubmit, disableAuthor }: TournamentFormProps) {
  const { t } = useTranslate(['malyfotbal', 'general']);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [saving, setSaving] = useState(false);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TournamentFormData>({
    // @ts-ignore
    resolver: yupResolver(schema),
    defaultValues: values,
  });

  const verifyReCaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    await executeRecaptcha('tournamentForm');
  }, [executeRecaptcha]);

  useEffect(() => {
    verifyReCaptcha();
  }, [verifyReCaptcha]);

  const saveForm = useCallback(
    async (data: TournamentFormData) => {
      setSaving(true);
      await onSubmit(data);
      setSaving(false);
    },
    [onSubmit],
  );

  const updateCoords = (coords: Coords | null) => {
    setValue('latitude', coords?.lat ?? null, { shouldValidate: true });
    setValue('longitude', coords?.lng ?? null, { shouldValidate: true });
  };

  return (
    <div className='TournamentForm'>
      <form onSubmit={handleSubmit(saveForm)}>
        <div className='TournamentForm__section'>
          <h2>{t('tournaments.form.section.personal')}</h2>
          <div className='TournamentForm__field'>
            <div className='TournamentForm__field--split'>
              <Controller
                name='givenName'
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('tournaments.form.givenName')}
                    type='text'
                    autoCapitalize='on'
                    disabled={disableAuthor}
                    error={errors.givenName?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name='familyName'
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('tournaments.form.familyName')}
                    type='text'
                    autoCapitalize='on'
                    disabled={disableAuthor}
                    error={errors.familyName?.message}
                    {...field}
                  />
                )}
              />
            </div>
            <p className='TournamentForm__helper'>{t('tournaments.form.familyName.helper')}</p>
          </div>
          <div className='TournamentForm__field'>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextField
                  label={t('tournaments.form.email')}
                  type='email'
                  disabled={disableAuthor}
                  error={errors.email?.message}
                  {...field}
                />
              )}
            />
            <p className='TournamentForm__helper'>{t('tournaments.form.email.helper')}</p>
          </div>
          <div className='TournamentForm__field'>
            <Controller
              name='phone'
              control={control}
              render={({ field }) => (
                <TextField
                  label={t('tournaments.form.phone')}
                  type='text'
                  error={errors.phone?.message}
                  {...field}
                />
              )}
            />
            <p className='TournamentForm__helper'>{t('tournaments.form.phone.helper')}</p>
          </div>
        </div>
        <div className='TournamentForm__section'>
          <h2>{t('tournaments.form.section.general')}</h2>
          <div className='TournamentForm__field TournamentForm__field--small'>
            <Controller
              name='category'
              control={control}
              render={({ field }) => (
                <SelectField
                  label={t('tournaments.form.category')}
                  error={errors.category?.message}
                  {...field}
                >
                  {categories.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </SelectField>
              )}
            />
            <p className='TournamentForm__helper'>{t('tournaments.form.category.helper')}</p>
          </div>
          <div className='TournamentForm__field'>
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <TextField
                  label={t('tournaments.form.name')}
                  type='text'
                  error={errors.name?.message}
                  {...field}
                />
              )}
            />
            <p className='TournamentForm__helper'>{t('tournaments.form.name.helper')}</p>
          </div>
          <div className='TournamentForm__field TournamentForm__field--small'>
            <Controller
              name='year'
              control={control}
              render={({ field }) => (
                <TextField
                  label={t('tournaments.form.year')}
                  type='text'
                  error={errors.year?.message}
                  {...field}
                />
              )}
            />
            <p className='TournamentForm__helper'>{t('tournaments.form.year.helper')}</p>
          </div>
          <div className='TournamentForm__field TournamentForm__field--split'>
            <div className='TournamentForm__field TournamentForm__field--small TournamentForm__field--noMargin'>
              <Controller
                name='playType'
                control={control}
                render={({ field }) => (
                  <SelectField
                    label={t('tournaments.form.playType')}
                    error={errors.playType?.message}
                    {...field}
                  >
                    {Object.values(TournamentPlayType).map(type => (
                      <option key={type} value={type}>
                        {t(`tournaments.form.playType.option.${type}`)}
                      </option>
                    ))}
                  </SelectField>
                )}
              />
              <p className='TournamentForm__helper'>{t('tournaments.form.playType.helper')}</p>
            </div>
            <div className='TournamentForm__field TournamentForm__field--small TournamentForm__field--noMargin'>
              <Controller
                name='groundType'
                control={control}
                render={({ field }) => (
                  <SelectField
                    label={t('tournaments.form.groundType')}
                    error={errors.groundType?.message}
                    {...field}
                  >
                    {Object.values(TournamentGroundType).map(type => (
                      <option key={type} value={type}>
                        {t(`tournaments.form.groundType.option.${type}`)}
                      </option>
                    ))}
                  </SelectField>
                )}
              />
              <p className='TournamentForm__helper'>{t('tournaments.form.groundType.helper')}</p>
            </div>
          </div>
          <div className='TournamentForm__field'>
            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <TextArea
                  label={t('tournaments.form.description')}
                  error={errors.description?.message}
                  {...field}
                />
              )}
            />
            <p className='TournamentForm__helper'>{t('tournaments.form.description.helper')}</p>
          </div>
          <div className='TournamentForm__field'>
            <Controller
              name='link'
              control={control}
              render={({ field }) => (
                <TextField
                  label={t('tournaments.form.link')}
                  type='url'
                  error={errors.link?.message}
                  {...field}
                />
              )}
            />
            <p className='TournamentForm__helper'>{t('tournaments.form.link.helper')}</p>
          </div>
          <div className='TournamentForm__field TournamentForm__field--small'>
            <Controller
              name='fee'
              control={control}
              render={({ field }) => (
                <TextField
                  label={t('tournaments.form.fee')}
                  type='number'
                  step='0.01'
                  min='0'
                  accessory='Kč'
                  error={errors.fee?.message}
                  {...field}
                />
              )}
            />
            <p className='TournamentForm__helper'>{t('tournaments.form.fee.helper')}</p>
          </div>
        </div>
        <div className='TournamentForm__section'>
          <h2>{t('tournaments.form.section.place')}</h2>
          <div className='TournamentForm__field TournamentForm__field--split'>
            <Controller
              name='region'
              control={control}
              render={({ field }) => (
                <SelectField
                  label={t('tournaments.form.region')}
                  error={errors.region?.message}
                  {...field}
                >
                  {Regions.map(key => (
                    <option key={key} value={key}>
                      {t(`region.${key}`)}
                    </option>
                  ))}
                </SelectField>
              )}
            />
            <Controller
              name='city'
              control={control}
              render={({ field }) => (
                <TextField
                  label={t('tournaments.form.city')}
                  type='text'
                  error={errors.city?.message}
                  {...field}
                />
              )}
            />
          </div>
          <TournamentFormMap
            defaultCoords={
              values.latitude && values.longitude
                ? { lat: values.latitude, lng: values.longitude }
                : undefined
            }
            onChange={updateCoords}
            error={errors.latitude?.message ?? errors.longitude?.message}
          />
        </div>
        <div className='TournamentForm__section'>
          <h2>{t('tournaments.form.section.date')}</h2>
          <div className='TournamentForm__field'>
            <div className='TournamentForm__field--split'>
              <Controller
                name='dateFrom'
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('tournaments.form.dateFrom')}
                    type='date'
                    error={errors.dateFrom?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name='dateTo'
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t('tournaments.form.dateTo')}
                    type='date'
                    error={errors.dateTo?.message}
                    {...field}
                  />
                )}
              />
            </div>
            <p className='TournamentForm__helper'>{t('tournaments.form.dateTo.helper')}</p>
          </div>
        </div>
        <div className='TournamentForm__action'>
          <Button type='submit' variant='blue' disabled={saving}>
            {t('tournaments.form.create')}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default withGoogleReCaptcha<TournamentFormProps>(TournamentForm);
